import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Loader2 } from "lucide-react";

interface ConfirmModalProps {
  open: boolean;
  title: string;
  message?: string;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
  // "warning" tints the confirm button amber for destructive/sensitive actions.
  tone?: "default" | "warning";
}

// Centered, accessible confirmation dialog. Reuses the Drawer a11y recipe
// (portal-to-body, role="dialog", focus trap + restore) but is built to open
// ON TOP OF the Drawer, so it is careful about two nested-dialog hazards:
//   1. Escape — the Drawer listens for Escape on `document`. We register our own
//      handler in the CAPTURE phase and stopImmediatePropagation so the Drawer
//      never sees it (otherwise one Escape would close both).
//   2. inert — the Drawer already set `inert` on #root. We inert every other
//      body-level layer (including the Drawer panel) to trap focus here, but we
//      SKIP nodes that are already inert and only restore the ones we set, so we
//      don't strip the Drawer's own trap when we close.
export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  busy,
  tone = "default",
}: ConfirmModalProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descId = useId();
  const isWarning = tone === "warning";

  // Escape (capture phase) — preempt the parent Drawer's document-level handler.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopImmediatePropagation();
        e.preventDefault();
        if (!busy) onCancel();
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, busy, onCancel]);

  // Focus trap (nested-safe) + restore.
  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement as HTMLElement | null;
    const wrapper = wrapperRef.current;
    const silenced: HTMLElement[] = [];
    for (const node of Array.from(document.body.children)) {
      if (node === wrapper) continue;
      const el = node as HTMLElement;
      if (el.hasAttribute("inert")) continue; // already trapped (e.g. #root by the Drawer)
      el.setAttribute("inert", "");
      el.setAttribute("aria-hidden", "true");
      silenced.push(el);
    }
    // Destructive/sensitive (warning) actions default focus to the SAFE button
    // (Cancel) so a stray Enter/Space can't trigger the dangerous confirm.
    (isWarning ? cancelRef : confirmRef).current?.focus();
    return () => {
      for (const el of silenced) {
        el.removeAttribute("inert");
        el.removeAttribute("aria-hidden");
      }
      restoreRef.current?.focus?.();
    };
  }, [open, isWarning]);

  if (!open) return null;

  return createPortal(
    <div ref={wrapperRef} className="fixed inset-0 z-[60] grid place-items-center p-4">
      <div
        className="modal-scrim absolute inset-0 bg-grey-900/40"
        onClick={busy ? undefined : onCancel}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={message ? descId : undefined}
        className="modal-pop relative w-full max-w-[400px] rounded-card bg-white p-5 shadow-float"
      >
        <div className="flex gap-3.5">
          {isWarning && (
            <span
              aria-hidden
              className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-warning-bg text-warning"
            >
              <AlertTriangle size={18} />
            </span>
          )}
          <div className="min-w-0 flex-1 pt-0.5">
            <h3 id={titleId} className="text-[15px] font-bold text-grey-900">
              {title}
            </h3>
            {message && (
              <p id={descId} className="mt-1.5 text-[13px] leading-snug text-grey-600">
                {message}
              </p>
            )}
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="flex h-9 items-center rounded-control border border-grey-300 bg-white px-3.5 text-[13px] font-semibold text-grey-700 hover:bg-grey-50 disabled:opacity-60"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className={`flex h-9 items-center gap-1.5 rounded-control px-3.5 text-[13px] font-semibold text-white disabled:opacity-60 ${
              isWarning ? "bg-warning hover:opacity-90" : "bg-green hover:bg-green-dark"
            }`}
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
